/**
 * Утилита для валидации интервала дат
 */

/**
 * Проверяет корректность интервала дат в формате dd.mm.yyyy-dd.mm.yyyy
 * Возвращает true, если вторая дата больше или равна первой
 *
 * @param dateInterval - строка интервала дат в формате "dd.mm.yyyy-dd.mm.yyyy"
 * @returns boolean - true если интервал корректный, false если нет
 *
 * @example
 * validateDateInterval('01.01.2023-31.12.2023') // true
 * validateDateInterval('31.12.2023-01.01.2023') // false
 * validateDateInterval('01.01.2023-01.01.2023') // true (равные даты)
 * validateDateInterval('invalid-format') // false
 */
export function validateDateInterval (dateInterval: string): boolean {
  if (!dateInterval || typeof dateInterval !== 'string') {
    return false;
  }

  const intervalPattern = /^(\d{2}\.\d{2}\.\d{4})-(\d{2}\.\d{2}\.\d{4})$/;
  const match = dateInterval.match(intervalPattern);

  if (!match) {
    return false;
  }

  const [, startDateStr, endDateStr] = match;

  try {
    const startDate = parseDateFromDDMMYYYY(startDateStr);
    const endDate = parseDateFromDDMMYYYY(endDateStr);

    if (!startDate || !endDate) {
      return false;
    }

    return endDate >= startDate;
  } catch (error) {
    return false;
  }
}

/**
 * Парсит дату из строки формата dd.mm.yyyy в объект Date
 *
 * @param dateStr - строка даты в формате dd.mm.yyyy
 * @returns Date | null - объект Date или null если дата невалидная
 */
function parseDateFromDDMMYYYY (dateStr: string): Date | null {
  const datePattern = /^(\d{2})\.(\d{2})\.(\d{4})$/;
  const match = dateStr.match(datePattern);

  if (!match) {
    return null;
  }

  const [, day, month, year] = match;
  const dayNum = Number.parseInt(day, 10);
  const monthNum = Number.parseInt(month, 10) - 1; // Месяцы в Date начинаются с 0
  const yearNum = Number.parseInt(year, 10);

  // Создаем дату
  const date = new Date(yearNum, monthNum, dayNum);

  // Проверяем что дата корректная (не было переполнения)
  if (
    date.getFullYear() !== yearNum
    || date.getMonth() !== monthNum
    || date.getDate() !== dayNum
  ) {
    return null;
  }

  return date;
}

/**
 * Извлекает отдельные даты из строки интервала
 *
 * @param dateInterval - строка интервала дат в формате "dd.mm.yyyy-dd.mm.yyyy"
 * @returns { start: string, end: string } | null - объект с начальной и конечной датой или null
 *
 * @example
 * extractDatesFromInterval('01.01.2023-31.12.2023')
 * // { start: '01.01.2023', end: '31.12.2023' }
 */
export function extractDatesFromInterval (dateInterval: string): { start: string; end: string } | null {
  if (!dateInterval || typeof dateInterval !== 'string') {
    return null;
  }

  const intervalPattern = /^(\d{2}\.\d{2}\.\d{4})-(\d{2}\.\d{2}\.\d{4})$/;
  const match = dateInterval.match(intervalPattern);

  if (!match) {
    return null;
  }

  const [, start, end] = match;
  return { start, end };
}
